// Banner Editor — modal with AI assist · split from SponsorHQ.jsx
import { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function BannerEditor({ onClose }) {
  const [form, setForm] = useState({
    name: "Aurora Glow campaign",
    headline: "Aurora · taste the night",
    body: "Limited drop. Live now on NEXUS.",
    cta_label: "Shop drop",
    cta_url: "https://elysium.live/drops",
    bg_color: "#0a0e14",
    text_color: "#00F0FF",
    accent_color: "#FFC107",
    objective: "reach",
    format: "30s",
    duration_days: 7,
    creative_b64: null,
  });
  const [cost, setCost] = useState(null);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/sponsor/campaign/cost", { params: { format: form.format, duration_days: form.duration_days } })
      .then(({ data }) => setCost(data));
  }, [form.format, form.duration_days]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 5_000_000) { toast.error("Max 5MB"); return; }
    const r = new FileReader();
    r.onload = () => { setForm((F) => ({ ...F, creative_b64: r.result })); toast.success("Banner image attached"); };
    r.readAsDataURL(f);
  };

  const clearImage = () => setForm((F) => ({ ...F, creative_b64: null }));

  const askAi = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sponsor/ai-assist", { topic: form.headline, objective: form.objective, format: form.format, duration_days: form.duration_days });
      try {
        const j = JSON.parse(data.raw);
        setAi({ ...j, fallback: data.fallback });
      } catch {
        setAi({ headline: data.raw, body: "", reasoning: "AI returned non-JSON. Showing raw text." });
      }
    } catch (e) { toast.error("AI assist failed"); }
    setBusy(false);
  };

  const applyAi = () => {
    if (!ai) return;
    setForm((F) => ({
      ...F,
      headline: ai.headline || F.headline,
      body: ai.body || F.body,
      accent_color: ai.accent_color || F.accent_color,
      format: ai.suggested_format || F.format,
    }));
    toast.success("AI suggestion applied");
  };

  const save = async (publish = false) => {
    try {
      const { data } = await api.post("/sponsor/campaign/draft", form);
      toast.success("Draft saved · " + cost?.nx + " NX");
      if (publish) {
        try {
          await api.post(`/sponsor/campaign/${data.id}/publish`);
          toast.success("Campaign LIVE · auto-rotating across NEXUS");
        } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed (saved as draft)"); }
      }
      onClose();
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={onClose}>
      <div className="glass-strong w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="banner-editor">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl">Banner Editor · Advertiser Studio</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">✕</button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT — editor */}
          <div className="space-y-3">
            <div><div className="label-eyebrow mb-1">campaign name</div><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" /></div>
            <div><div className="label-eyebrow mb-1">headline</div><input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="banner-headline" /></div>
            <div><div className="label-eyebrow mb-1">body (one line)</div><input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" /></div>
            <div className="grid grid-cols-3 gap-2">
              {["bg_color", "text_color", "accent_color"].map((k) => (
                <div key={k}>
                  <div className="label-eyebrow mb-1">{k.replace("_", " ")}</div>
                  <input type="color" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full h-10 bg-transparent border border-white/10" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><div className="label-eyebrow mb-1">CTA label</div><input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" /></div>
              <div><div className="label-eyebrow mb-1">CTA URL</div><input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="label-eyebrow mb-1">format</div>
                <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm" data-testid="banner-format">
                  {["15s","30s","takeover","always-on"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <div className="label-eyebrow mb-1">days</div>
                <input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">objective</div>
                <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
                  {["reach","clicks","follows"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="border border-nexus-cyan/30 bg-nexus-cyan/5 p-3 space-y-2" data-testid="banner-image-upload">
              <div className="label-eyebrow text-nexus-cyan">// banner image · logo / hero shot</div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
              {form.creative_b64 ? (
                <div className="flex items-center gap-3">
                  <img src={form.creative_b64} alt="" className="w-16 h-16 object-cover ring-1 ring-nexus-cyan/60" data-testid="banner-image-preview" />
                  <div className="flex-1 text-[11px] text-white/60">Image attached. Appears in the preview on the right.</div>
                  <button onClick={() => fileRef.current?.click()} className="btn-ghost" data-testid="banner-replace-image">Replace</button>
                  <button onClick={clearImage} className="btn-ghost !border-nexus-crimson !text-nexus-crimson" data-testid="banner-remove-image">Remove</button>
                </div>
              ) : (
                <button onClick={() => fileRef.current?.click()} className="btn-ghost w-full justify-center !border-nexus-cyan !text-nexus-cyan" data-testid="banner-upload-image">
                  📷 Upload banner image (JPG/PNG · max 5MB)
                </button>
              )}
              <p className="text-[10px] text-white/40">Used on Watch banners, Reel popup ads, and the rotating Watch carousel. Recommended 1:1 ratio for logos, 16:9 for hero shots.</p>
            </div>

            <div className="bg-nexus-panel p-3 border border-nexus-amber/30 mt-2">
              <div className="flex items-center justify-between">
                <div className="label-eyebrow text-nexus-amber">// estimated cost</div>
                <div className="font-display text-2xl text-nexus-amber" data-testid="banner-cost">{cost?.nx || "…"} NX</div>
              </div>
              <div className="text-[10px] text-white/50 mt-1">≈ {cost?.impressions?.toLocaleString()} impressions · ${cost?.aud} AUD · {form.format} · {form.duration_days}d</div>
            </div>

            <button onClick={askAi} disabled={busy} className="btn-ghost w-full justify-center !border-nexus-violet !text-nexus-violet" data-testid="banner-ai-assist">
              ✨ {busy ? "ED3N thinking…" : "AI Assist — suggest cheaper variant"}
            </button>
            {ai && (
              <div className="bg-nexus-panel border border-nexus-violet/40 p-3 space-y-2 text-xs" data-testid="ai-suggestion">
                <div className="text-nexus-violet tracking-[0.3em] uppercase text-[9px] font-display">// ed3n suggestion</div>
                {ai.headline && <div><strong>Headline:</strong> {ai.headline}</div>}
                {ai.body && <div><strong>Body:</strong> {ai.body}</div>}
                {ai.suggested_format && <div><strong>Format:</strong> {ai.suggested_format}</div>}
                {ai.accent_color && <div className="flex items-center gap-2"><strong>Accent:</strong><span className="inline-block w-4 h-4" style={{ background: ai.accent_color }} /> {ai.accent_color}</div>}
                {ai.reasoning && <div className="text-white/60 italic">{ai.reasoning}</div>}
                <button onClick={applyAi} className="btn-nexus text-[10px]" data-testid="apply-ai">Apply suggestion</button>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/10">
              <button onClick={() => save(false)} className="btn-ghost">Save draft</button>
              <button onClick={() => save(true)} className="btn-nexus" data-testid="banner-publish">Publish ({cost?.nx} NX)</button>
            </div>
          </div>

          {/* RIGHT — preview */}
          <div className="space-y-3">
            <div className="label-eyebrow text-nexus-cyan">// live preview · matches /watch banner</div>
            <div className="relative overflow-hidden border" style={{ borderColor: form.accent_color, background: `linear-gradient(135deg, ${form.bg_color}, ${form.bg_color}cc)` }} data-testid="banner-preview">
              <div className="absolute top-0 left-0 px-2 py-0.5 text-[8px] tracking-[0.3em] font-display font-black uppercase" style={{ background: form.accent_color, color: form.bg_color }}>★ Sponsored</div>
              <div className="p-5 flex items-center gap-4">
                {form.creative_b64 && <img src={form.creative_b64} alt="" className="w-16 h-16 object-cover" />}
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.3em] uppercase font-display" style={{ color: form.accent_color }}>{form.objective}</div>
                  <div className="font-display font-bold text-lg mt-0.5" style={{ color: form.text_color }}>{form.headline}</div>
                  <div className="text-[11px] mt-1" style={{ color: form.text_color, opacity: 0.7 }}>{form.body}</div>
                </div>
                <div className="px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-display font-bold" style={{ background: form.accent_color, color: form.bg_color }}>{form.cta_label}</div>
              </div>
            </div>
            <div className="text-[10px] text-white/40 leading-relaxed">
              This banner will auto-rotate across every NEXUS Watch page and member profile (skipping campaigns owned by the same channel). Per-impression revenue: <span className="text-nexus-amber">500 NX per 1,000 impressions</span> — creators earn 70%, platform takes 30%.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
